import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Pages.module.css';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  
  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.container}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1>Frequently Asked Questions</h1>
        </div>
      </div>
      
      <div className={styles.pageContent}>
        <div className={styles.container}>
          <div className={styles.faqCategoryTabs}>
            <button 
              className={`${styles.categoryTab} ${activeCategory === 'general' ? styles.activeTab : ''}`}
              onClick={() => setActiveCategory('general')}
            >
              General
            </button>
            <button 
              className={`${styles.categoryTab} ${activeCategory === 'staking' ? styles.activeTab : ''}`}
              onClick={() => setActiveCategory('staking')}
            >
              Staking
            </button>
            <button 
              className={`${styles.categoryTab} ${activeCategory === 'rewards' ? styles.activeTab : ''}`}
              onClick={() => setActiveCategory('rewards')}
            >
              Rewards
            </button>
            <button 
              className={`${styles.categoryTab} ${activeCategory === 'wallets' ? styles.activeTab : ''}`}
              onClick={() => setActiveCategory('wallets')}
            >
              Wallets
            </button>
            <button 
              className={`${styles.categoryTab} ${activeCategory === 'troubleshooting' ? styles.activeTab : ''}`}
              onClick={() => setActiveCategory('troubleshooting')}
            >
              Troubleshooting
            </button>
          </div>
          
          {activeCategory === 'general' && (
            <div className={styles.faqCategory}>
              <div className={styles.faqItem}>
                <h3>What is NexusStake?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    NexusStake is a secure platform for staking cryptocurrency tokens. We provide a user-friendly 
                    interface for users to stake their tokens and earn rewards over time. Our platform is built 
                    with enterprise-grade security features to ensure the safety of your assets.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>How does staking work?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    Staking is the process of locking your cryptocurrency assets to support blockchain network 
                    operations while earning passive income. When you stake your tokens, they are locked in a 
                    smart contract, and you receive rewards based on the amount staked and the reward rate.
                  </p>
                  <p>
                    For more detailed information about the staking process, please visit our 
                    <Link to="/docs"> Documentation</Link> page.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>Is staking safe?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    Staking on NexusStake is designed to be secure. Our smart contracts have undergone 
                    independent security audits, and we use best practices to protect user funds. However, 
                    as with any blockchain operation, there are inherent risks involved.
                  </p>
                  <p>
                    We recommend only staking amounts you can afford to lose and familiarizing yourself 
                    with our security measures in the <Link to="/docs#security">Security section</Link> of our documentation.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>What blockchain networks do you support?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    Currently, NexusStake operates on the Ethereum blockchain. We plan to expand to other 
                    networks in the future, including Polygon, Binance Smart Chain, and others.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>How do I contact customer support?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    If you need assistance with the platform, you can reach our support team via:
                  </p>
                  <ul>
                    <li>Email: support@nexusstake.com</li>
                    <li>Discord: Join our <a href="https://discord.gg/nexusstake" target="_blank" rel="noopener noreferrer">Discord server</a></li>
                    <li>Telegram: Join our <a href="https://t.me/nexusstake" target="_blank" rel="noopener noreferrer">Telegram group</a></li>
                  </ul>
                  <p>
                    Our support team is available 24/7 to assist you with any issues or questions you might have.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {activeCategory === 'staking' && (
            <div className={styles.faqCategory}>
              <div className={styles.faqItem}>
                <h3>What is the minimum amount I can stake?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    The minimum staking amount on our platform is 100 tokens. There is no maximum limit, 
                    so you can stake as many tokens as you wish.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>How do I stake my tokens?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    To stake your tokens, follow these steps:
                  </p>
                  <ol>
                    <li>Connect your wallet to the platform</li>
                    <li>Ensure you have sufficient tokens in your wallet</li>
                    <li>Enter the amount you want to stake in the input field</li>
                    <li>Click the "Stake" button</li>
                    <li>Confirm the transaction in your wallet</li>
                    <li>Wait for the transaction to be confirmed on the blockchain</li>
                  </ol>
                  <p>
                    After the transaction is confirmed, your tokens will be locked, and you'll start earning rewards.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>Is there a lock-up period for staking?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    No, there is no mandatory lock-up period on our platform. You can unstake your tokens 
                    at any time. However, you will stop earning rewards on the unstaked tokens.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>Are there any fees for staking or unstaking?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    NexusStake does not charge any platform fees for staking or unstaking. However, you will 
                    need to pay standard network fees (gas) for performing transactions on the Ethereum blockchain.
                  </p>
                  <p>
                    The amount of gas fees varies depending on network congestion. You can check the current 
                    gas prices on <a href="https://etherscan.io/gastracker" target="_blank" rel="noopener noreferrer">Etherscan's Gas Tracker</a>.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>Can I add more tokens to my existing stake?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    Yes, you can add more tokens to your existing stake at any time. Simply enter the additional 
                    amount you want to stake and complete the transaction. The new tokens will be added to your 
                    existing stake, and rewards will be calculated accordingly.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {activeCategory === 'rewards' && (
            <div className={styles.faqCategory}>
              <div className={styles.faqItem}>
                <h3>What is the current reward rate (APY)?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    Our current Annual Percentage Yield (APY) is 12%. This rate may be adjusted over time 
                    based on market conditions and platform performance.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>How often are rewards calculated?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    Rewards are calculated in real-time and accrue with each new block on the Ethereum blockchain 
                    (approximately every 15 seconds). You can see your accumulated rewards in the staking interface.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>When can I claim my rewards?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    You can claim your accumulated rewards at any time by clicking the "Claim Rewards" 
                    button in the staking interface. There is no minimum threshold for claiming rewards.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>How are rewards calculated?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    Rewards are calculated based on the following formula:
                  </p>
                  <div className={styles.codeBlock}>
                    Daily Rewards = (Staked Amount × APY) ÷ 365
                  </div>
                  <p>
                    For example, if you stake 1,000 tokens with a 12% APY, your daily rewards would be:
                    (1,000 × 0.12) ÷ 365 = 0.329 tokens per day.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>Do I need to claim rewards regularly, or can I let them accumulate?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    You can let your rewards accumulate for as long as you want. However, unclaimed rewards 
                    do not compound automatically. To maximize your returns, you may want to claim and restake 
                    your rewards periodically to benefit from compound interest.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {activeCategory === 'wallets' && (
            <div className={styles.faqCategory}>
              <div className={styles.faqItem}>
                <h3>Which wallets are compatible with NexusStake?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    NexusStake supports the following wallets:
                  </p>
                  <ul>
                    <li>MetaMask</li>
                    <li>Trust Wallet</li>
                    <li>WalletConnect</li>
                    <li>Coinbase Wallet</li>
                    <li>Fortmatic</li>
                    <li>Hardware wallets (Ledger, Trezor) via MetaMask or WalletConnect</li>
                  </ul>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>How do I set up MetaMask?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    To set up MetaMask, follow these steps:
                  </p>
                  <ol>
                    <li>Visit the <a href="https://metamask.io/download/" target="_blank" rel="noopener noreferrer">MetaMask website</a> and download the extension for your browser</li>
                    <li>Click "Create a Wallet" if you're new, or "Import Wallet" if you have an existing wallet</li>
                    <li>Follow the instructions to create a password and backup your seed phrase (keep this secure!)</li>
                    <li>Once set up, connect to the Ethereum Mainnet</li>
                    <li>Add your tokens to MetaMask if they're not automatically visible</li>
                  </ol>
                  <p>
                    For a more detailed guide, check MetaMask's official <a href="https://metamask.io/faqs/" target="_blank" rel="noopener noreferrer">documentation</a>.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>Why can't I see my tokens in MetaMask?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    If you can't see your tokens in MetaMask, you may need to add them manually:
                  </p>
                  <ol>
                    <li>Open MetaMask and click on "Assets"</li>
                    <li>Scroll down and click "Import tokens"</li>
                    <li>Enter the token contract address (available on our homepage)</li>
                    <li>The token symbol and decimals should auto-fill</li>
                    <li>Click "Add Custom Token" and then "Import Tokens"</li>
                  </ol>
                  <p>
                    After following these steps, your tokens should be visible in MetaMask.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>How do I disconnect my wallet from NexusStake?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    To disconnect your wallet from our platform:
                  </p>
                  <ol>
                    <li>For MetaMask: Click on the MetaMask extension, click the three dots menu, select "Connected sites," find NexusStake, and click "Disconnect"</li>
                    <li>For other wallets: Refer to your wallet's documentation for disconnecting from websites</li>
                  </ol>
                  <p>
                    You can also refresh the page or close your browser, which will disconnect your session, but your wallet may still show as authorized to connect in the future.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {activeCategory === 'troubleshooting' && (
            <div className={styles.faqCategory}>
              <div className={styles.faqItem}>
                <h3>My transaction is pending for a long time. What should I do?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    If your transaction is pending for an extended period, it may be due to low gas price or network congestion. You can:
                  </p>
                  <ol>
                    <li>Wait for the network to process your transaction (this could take hours during high congestion)</li>
                    <li>Speed up your transaction by increasing the gas price (in MetaMask, you can click on the pending transaction and select "Speed Up")</li>
                    <li>Cancel the transaction if it's not processed yet (in MetaMask, click on the pending transaction and select "Cancel")</li>
                  </ol>
                  <p>
                    For more advanced users, you can use tools like <a href="https://etherscan.io/gastracker" target="_blank" rel="noopener noreferrer">Etherscan's Gas Tracker</a> to find optimal gas prices.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>I approved tokens for staking, but the transaction failed. Why?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    Several factors could cause a transaction to fail:
                  </p>
                  <ul>
                    <li>Insufficient gas: The gas limit was too low for the transaction</li>
                    <li>Gas price too low: The network prioritized other transactions with higher gas prices</li>
                    <li>Insufficient token balance: You don't have enough tokens for the transaction</li>
                    <li>Insufficient ETH: You don't have enough ETH to pay for gas</li>
                    <li>Smart contract error: There might be an issue with the contract conditions</li>
                  </ul>
                  <p>
                    Check the transaction details on Etherscan by looking up your transaction hash to see the specific reason for failure.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>The website isn't loading properly. How can I fix this?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    If the website isn't loading correctly, try these troubleshooting steps:
                  </p>
                  <ol>
                    <li>Refresh the page</li>
                    <li>Clear your browser cache and cookies</li>
                    <li>Try a different browser</li>
                    <li>Disable browser extensions that might interfere with the site</li>
                    <li>Check your internet connection</li>
                    <li>Try accessing the site in incognito/private browsing mode</li>
                  </ol>
                  <p>
                    If the problem persists, please contact our support team with details about the issue, including screenshots if possible.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>My balance isn't updating after staking. What's wrong?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    If your balance isn't updating after a successful staking transaction:
                  </p>
                  <ol>
                    <li>Verify that the transaction was successful on the blockchain (check the transaction hash on Etherscan)</li>
                    <li>Refresh the page to update the interface</li>
                    <li>Check if your wallet is connected to the correct network</li>
                    <li>Try disconnecting and reconnecting your wallet</li>
                  </ol>
                  <p>
                    In some cases, there might be a slight delay in displaying updated balances due to blockchain confirmation times or caching.
                  </p>
                </div>
              </div>
              
              <div className={styles.faqItem}>
                <h3>I'm having issues connecting my wallet. What should I do?</h3>
                <div className={styles.faqAnswer}>
                  <p>
                    If you're experiencing issues connecting your wallet:
                  </p>
                  <ol>
                    <li>Make sure your wallet is unlocked</li>
                    <li>Verify you're using a supported wallet and browser combination</li>
                    <li>Check if you're connected to the correct network (Ethereum Mainnet)</li>
                    <li>Try refreshing the page</li>
                    <li>Restart your browser or try a different browser</li>
                    <li>Update your wallet software to the latest version</li>
                  </ol>
                  <p>
                    If you continue to experience issues, please contact our support team for assistance.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className={styles.supportContact}>
            <h3>Still have questions?</h3>
            <p>
              Our support team is available 24/7 to assist you with any questions or issues.
              Contact us at <a href="mailto:support@nexusstake.com">support@nexusstake.com</a> or
              join our <a href="https://discord.gg/nexusstake" target="_blank" rel="noopener noreferrer">Discord community</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ; 